import { ChangeEvent, useMemo, useState } from 'react';
import { CoreBox, CoreCheckbox, CoreFormControlLabel, CoreTooltip, CoreTypography } from '@youscience/core';
import { majorsDescriptionMap } from '@common-types';

import useHomePageStore from '@store/HomePageStore';

import { INITIAL_VALUES as Initial, ShowMoreOptions } from '@interfaces/adminHomePage';

import {
  OTHER_DEGREE_DATA,
  CERTIFICATE_PROGRAMS_DATA,
  DOCTORATE_PROGRAMS_DATA,
  FIELD_VALUES,
  INITIAL_VALUES,
} from '@constants/adminHomePage';

import ChevronBottomIcon from '@images/chevron-bottom.svg?react';

import { sxStyles, StyledAdditionalPrograms } from './MajorData.styles';

interface MajorDataProps {
  cipCode: string;
  onMajorDataChange: (cipCode: string, awLevel: number[] | null, additionalParams: { shouldOmit?: boolean }) => void;
}

export const MajorData = (props: MajorDataProps) => {
  const { cipCode, onMajorDataChange } = props;

  const {
    adminHomePageData: {
      school: { completions },
    },
  } = useHomePageStore((state) => state);

  const existMajorData = useMemo(() => {
    return completions.find((item) => item.cipCode === cipCode);
  }, [completions]);

  const [awLevels, setAwLevels] = useState(() => {
    if (existMajorData) {
      const init = { ...INITIAL_VALUES } as Initial;

      existMajorData?.awLevel.forEach((item) => {
        Object.keys(FIELD_VALUES).forEach((key) => {
          if (FIELD_VALUES[key as keyof Initial].includes(item)) {
            init[key as keyof Initial] = [...init[key as keyof Initial], item];
          }
        });
      });

      return init;
    }

    return INITIAL_VALUES;
  });

  const [showMoreOptions, setShowMoreOptions] = useState<ShowMoreOptions>({
    isMajorSelect: !!existMajorData,
    isCertificate: !!awLevels.certificatePrograms.length,
    isDoctorate: !!awLevels.doctoratePrograms.length,
  });

  const updateShowMoreOptions = (fieldUpdates: Partial<ShowMoreOptions>) => {
    setShowMoreOptions((prevState) => ({
      ...prevState,
      ...fieldUpdates,
    }));
  };

  const updateAwLevel = (fieldUpdates: Partial<Initial>, additionalParams: { shouldOmit?: boolean } = {}) => {
    const nextState = { ...awLevels, ...fieldUpdates };

    const preparedAwLevel = [
      ...nextState.associates,
      ...nextState.masters,
      ...nextState.bachelors,
      ...nextState.certificatePrograms,
      ...nextState.doctoratePrograms,
    ];

    setAwLevels(nextState);

    onMajorDataChange(cipCode, preparedAwLevel, additionalParams);
  };

  const handleSingleChange = (programsName: keyof Initial) => (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      updateAwLevel({ [programsName]: [...awLevels[programsName], +event.target.value] });
    } else {
      updateAwLevel({ [programsName]: awLevels[programsName].filter((item) => item !== +event.target.value) });

      if (programsName === 'certificatePrograms' && awLevels.certificatePrograms.length === 1) {
        updateShowMoreOptions({ isCertificate: false });
      }

      if (programsName === 'doctoratePrograms' && awLevels.doctoratePrograms.length === 1) {
        updateShowMoreOptions({ isDoctorate: false });
      }
    }
  };

  const handleShowMoreChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateShowMoreOptions({
      [event.target.value]: event.target.checked,
      ...(event.target.value === 'isMajorSelect' && !event.target.checked
        ? { isCertificate: false, isDoctorate: false }
        : {}),
    });

    if (event.target.value === 'isMajorSelect') {
      updateAwLevel(INITIAL_VALUES, { shouldOmit: !event.target.checked });
    }

    if (event.target.value === 'isCertificate' && !event.target.checked) {
      updateAwLevel({ certificatePrograms: INITIAL_VALUES.certificatePrograms });
    }

    if (event.target.value === 'isDoctorate' && !event.target.checked) {
      updateAwLevel({ doctoratePrograms: INITIAL_VALUES.doctoratePrograms });
    }
  };

  return (
    <CoreBox>
      <CoreBox sx={sxStyles.majorCheckBox}>
        <CoreCheckbox
          data-testid="isMajorSelect"
          value="isMajorSelect"
          checked={showMoreOptions.isMajorSelect}
          onChange={handleShowMoreChange}
        />
        <CoreTypography variant="h6" component="h5">
          {majorsDescriptionMap.get(cipCode)?.cipTitle}
        </CoreTypography>
        &nbsp;
        <CoreTooltip title={majorsDescriptionMap.get(cipCode)?.cipDefinition}>
          <CoreTypography sx={sxStyles.cipCodeTooltip}>{`CIP ${cipCode}`}</CoreTypography>
        </CoreTooltip>
      </CoreBox>

      {showMoreOptions.isMajorSelect && (
        <CoreBox>
          <CoreBox sx={sxStyles.degrees}>
            <CoreTypography variant="overline" component="h6">
              Degrees offered:
            </CoreTypography>

            {showMoreOptions.isMajorSelect && Object.values(awLevels).every((item) => !item.length) ? (
              <CoreTypography sx={sxStyles.helperText} variant="caption" component="div">
                Please select at least 1 degree
              </CoreTypography>
            ) : null}

            <CoreFormControlLabel
              control={
                <CoreCheckbox
                  value={OTHER_DEGREE_DATA.Associate.awardLevel}
                  checked={awLevels.associates.includes(OTHER_DEGREE_DATA.Associate.awardLevel)}
                  onChange={handleSingleChange('associates')}
                />
              }
              label={OTHER_DEGREE_DATA.Associate.category}
            />

            <CoreFormControlLabel
              control={
                <CoreCheckbox
                  value="isCertificate"
                  checked={showMoreOptions.isCertificate}
                  onChange={handleShowMoreChange}
                />
              }
              label="Certificate program"
            />

            <CoreFormControlLabel
              control={
                <CoreCheckbox
                  value={OTHER_DEGREE_DATA.Master.awardLevel}
                  checked={awLevels.masters.includes(OTHER_DEGREE_DATA.Master.awardLevel)}
                  onChange={handleSingleChange('masters')}
                />
              }
              label={OTHER_DEGREE_DATA.Master.category}
            />

            <CoreFormControlLabel
              control={
                <CoreCheckbox
                  value="isDoctorate"
                  checked={showMoreOptions.isDoctorate}
                  onChange={handleShowMoreChange}
                />
              }
              label="Doctorate"
            />

            <CoreFormControlLabel
              control={
                <CoreCheckbox
                  value={OTHER_DEGREE_DATA.Bachelor.awardLevel}
                  checked={awLevels.bachelors.includes(OTHER_DEGREE_DATA.Bachelor.awardLevel)}
                  onChange={handleSingleChange('bachelors')}
                />
              }
              label={OTHER_DEGREE_DATA.Bachelor.category}
            />
          </CoreBox>

          {showMoreOptions.isCertificate && (
            <StyledAdditionalPrograms>
              <CoreBox sx={sxStyles.additionalProgramsTitle}>
                <ChevronBottomIcon />

                <CoreTypography variant="overline" component="h6">
                  Certificate Programs
                </CoreTypography>
              </CoreBox>

              {showMoreOptions.isCertificate && awLevels.certificatePrograms.length === 0 ? (
                <CoreTypography sx={sxStyles.helperText} variant="caption" component="div">
                  Please select at least 1 program
                </CoreTypography>
              ) : null}

              {CERTIFICATE_PROGRAMS_DATA.map(({ awardLevel, label }) => (
                <CoreFormControlLabel
                  key={awardLevel}
                  sx={sxStyles.additionalCheckBox}
                  control={
                    <CoreCheckbox
                      value={awardLevel}
                      checked={awLevels.certificatePrograms.includes(awardLevel)}
                      onChange={handleSingleChange('certificatePrograms')}
                    />
                  }
                  label={label}
                />
              ))}
            </StyledAdditionalPrograms>
          )}

          {showMoreOptions.isDoctorate && (
            <StyledAdditionalPrograms>
              <CoreBox sx={sxStyles.additionalProgramsTitle}>
                <ChevronBottomIcon />

                <CoreTypography variant="overline" component="h6">
                  Doctorate Programs
                </CoreTypography>
              </CoreBox>

              {showMoreOptions.isDoctorate && awLevels.doctoratePrograms.length === 0 ? (
                <CoreTypography sx={sxStyles.helperText} variant="caption" component="div">
                  Please select at least 1 program
                </CoreTypography>
              ) : null}

              {DOCTORATE_PROGRAMS_DATA.map(({ awardLevel, label }) => (
                <CoreFormControlLabel
                  key={awardLevel}
                  sx={sxStyles.additionalCheckBox}
                  control={
                    <CoreCheckbox
                      value={awardLevel}
                      checked={awLevels.doctoratePrograms.includes(awardLevel)}
                      onChange={handleSingleChange('doctoratePrograms')}
                    />
                  }
                  label={label}
                />
              ))}
            </StyledAdditionalPrograms>
          )}
        </CoreBox>
      )}
    </CoreBox>
  );
};
